import {put, all, take, select, fork, join} from 'redux-saga/effects';
import searchApi from '../api/search.api';
import * as ActionTypes from '../actions/types.action';
import {getBearerHeader} from '../helpers/api.helper';
import {processedGQLSearchResults} from '../helpers/data.helper';
import {excludeFeaturedAlreadyInBasket} from '../helpers/product.helper';
import {waitFor} from '../helpers/saga.helper';
import {
  buildRelatedProductsRequest,
  getDeliveryDate,
} from '../helpers/search.helper';
import {DEFAULT_BRANCH, getInitialPageSize} from '../constants/constants';

export default function* search() {
  while (true) {
    const action = yield take(ActionTypes.GET_SEARCH_RESULTS_REQUESTED);
    try {
      const getAuth = state => state.auth;
      const getBasket = state => state.basket;
      const getSettings = state => state.settings;
      const getBranchId = state => state.branch;
      let storeToken = yield select(getAuth);
      const basket = yield select(getBasket);
      const settings = yield select(getSettings);
      const headers = getBearerHeader(action.jjToken, storeToken);

      let b = action.params.b;
      const times = 5;
      const ms = 300;
      if (!storeToken) {
        storeToken = yield waitFor(getAuth, 3, ms);
      }
      // check if basket/settings/profile contains branchId
      if (storeToken) {
        const theBasket = yield waitFor(getBasket, times, ms);
        b = theBasket && theBasket.branchId;
      } else {
        b = yield waitFor(getBranchId, times, ms);
      }
      const deliveryDate = getDeliveryDate(
        basket,
        action.fulfillmentDetails,
        settings
      );
      let params = action.params;
      // decodeURIComponent(undefined) === 'undefined', which used to reach the
      // service as a literal search term on any URL without a `q` (e.g. a
      // curated-list browse).
      params.q = params.q ? decodeURIComponent(params.q) : params.q;
      if (deliveryDate) {
        params = Object.assign({}, params, {deliveryDate});
      }
      params = {...params, b: b || DEFAULT_BRANCH};
      if (!params.size) {
        params = {...params, size: getInitialPageSize()};
      }
      const searchResultsFork = yield fork(searchApi.search, params, headers);
      const result = yield join(searchResultsFork);
      if (!result || result.errors) {
        yield put({
          type: ActionTypes.GET_SEARCH_RESULTS_FAILED,
          message: result && result.errors && result.errors[0],
        });
        // `continue`, not `return`: this generator is the only listener for search requests, and
        // returning ended it for good - one failed search left the search box dead until a reload.
        continue;
      }
      const searchId =
        result &&
        result.data &&
        result.data.searchProducts &&
        result.data.searchProducts.searchId;
      // Peer-gap trial cards. Only ever present on the first page, so searchMore.saga.js
      // deliberately does not read them - re-injecting on scroll would duplicate the cards.
      const featured =
        (result &&
          result.data &&
          result.data.searchProducts &&
          result.data.searchProducts.featured) ||
        [];
      // Read as late as possible, so a product added while this search was in flight still counts
      // as being in the basket. The basket itself is already waited for above.
      const basketHashMap = yield select(state => state.basketHashMap);
      // Whether a search carries featured cards at all, how many, and where they sit are all
      // decided by graph-product-search and documented on the `featured` field in its schema —
      // capping them again here would render fewer than its impression log records as served.
      // The basket is the one thing it cannot see, so that filter stays. Applied at the response
      // rather than in the render, so the set of cards is fixed for the life of the search.
      const featuredForSearch = excludeFeaturedAlreadyInBasket(
        featured,
        basketHashMap
      );
      const searchProducts =
        (result && result.data && result.data.searchProducts) || null;
      yield all([
        put({
          type: ActionTypes.GET_SEARCH_RESULTS_SUCCESS,
          searchId,
          query: params.q,
          // Set only when the words typed found nothing and these results are for a spelling
          // correction instead - SearchPage then says "Showing results for <correctedQuery>".
          correctedQuery: searchProducts && searchProducts.correctedQuery,
          featuredResults: featuredForSearch.map(f => ({
            position: f.position,
            reason: f.reason,
            campaignId: f.campaignId,
            title: f.title,
            // Same normalisation as the ordinary results, or the featured card renders with
            // full-size images and no multi-buy prices.
            product: {
              ...processedGQLSearchResults([f.product])[0],
              _searchId: searchId,
            },
          })),
          searchResults:
            result &&
            result.data &&
            result.data.searchProducts &&
            result.data.searchProducts.results &&
            processedGQLSearchResults(
              result.data.searchProducts.results,
              searchId
            ),
        }),
        put({
          type: ActionTypes.GET_SEARCH_RESULTS_PAGE_SUCCESS,
          page:
            result &&
            result.data &&
            result.data.searchProducts &&
            result.data.searchProducts.page,
        }),
        put({
          type: ActionTypes.GET_SEARCH_AGGREGATE_SUCCESS,
          aggression:
            result &&
            result.data &&
            result.data.searchProducts &&
            result.data.searchProducts.facets,
        }),
      ]);
      // The strip under a short or empty search. graph-product-search decides whether there is
      // anything to show, so this only filters out searches the strip is never for (browses,
      // banners, curated lists). Requested, not called, so this loop is free for the next search.
      const relatedRequest = buildRelatedProductsRequest({
        params,
        searchProducts,
        featuredItemIds: featuredForSearch.map(
          f => f.product && f.product.itemId
        ),
      });
      if (relatedRequest) {
        yield put({
          type: ActionTypes.GET_RELATED_PRODUCTS_REQUESTED,
          request: relatedRequest,
          headers,
        });
      }
    } catch (error) {
      yield put({
        type: ActionTypes.GET_SEARCH_RESULTS_FAILED,
        error,
      });
    }
  }
}
